import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import apiService from '../../services/apiService';

const BlogManagement = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });

  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    status: 'Draft',
    excerpt: '',
    author: '',
    authorImage: '',
    authorBio: '',
    featuredImage: '',
    publishedDate: '10/20/2025 12:00 AM',
    tags: [],
    contentSections: []
  });

  const [newTag, setNewTag] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);

  // Fetch blogs from API
  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getBlogs(pagination.page, pagination.limit);
      
      // Transform API response to match our component structure
      const transformedBlogs = response.blogs.map(blog => ({
        id: blog.id,
        title: blog.title || '',
        slug: blog.slug || '',
        excerpt: blog.excerpt || '',
        author: blog.author || '',
        authorBio: blog.author_bio || '',
        authorImage: blog.author_image || '',
        featuredImage: blog.image || '',
        publishedAt: blog.published_at || '',
        tags: Array.isArray(blog.tags) ? blog.tags : [],
        content: Array.isArray(blog.content) ? blog.content : [],
        status: blog.is_published === 'published' ? 'Published' : 'Draft',
        date: blog.published_at ? blog.published_at.split('T')[0] : (blog.created_at ? blog.created_at.split('T')[0] : ''),
        createdAt: blog.created_at || '',
        updatedAt: blog.updated_at || '',
        views: 0 // API doesn't provide views, defaulting to 0
      }));
      
      setBlogs(transformedBlogs);
      setPagination({
        total: response.total,
        page: response.page,
        limit: response.limit
      });
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files[0] || ''
    }));
  };

  const addTag = () => {
    if (newTag.trim()) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (index) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };

  const addContentSection = () => {
    setFormData(prev => ({
      ...prev,
      contentSections: [...prev.contentSections, {
        heading: '',
        description: '',
        subSections: []
      }]
    }));
  };

  const updateContentSection = (sectionIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      contentSections: (prev.contentSections || []).map((section, index) => 
        index === sectionIndex ? { ...section, [field]: value } : section
      )
    }));
  };

  const addSubSection = (sectionIndex) => {
    setFormData(prev => ({
      ...prev,
      contentSections: (prev.contentSections || []).map((section, index) => 
        index === sectionIndex 
          ? { ...section, subSections: [...(section.subSections || []), { heading: '' }] }
          : section
      )
    }));
  };

  const updateSubSection = (sectionIndex, subSectionIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      contentSections: (prev.contentSections || []).map((section, index) => 
        index === sectionIndex 
          ? {
              ...section,
              subSections: (section.subSections || []).map((subSection, subIndex) => 
                subIndex === subSectionIndex ? { ...subSection, [field]: value } : subSection
              )
            }
          : section
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      
      // Helper function to convert file to base64
      const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          if (!file) {
            resolve(null);
            return;
          }
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = error => reject(error);
        });
      };
      
      // Convert files to base64 (fallback to existing images on edit if no new file selected)
      const featuredImageBase64 = formData.featuredImage
        ? await fileToBase64(formData.featuredImage)
        : (editingBlog && editingBlog.featuredImage ? editingBlog.featuredImage : null);
      const authorImageBase64 = formData.authorImage
        ? await fileToBase64(formData.authorImage)
        : (editingBlog && editingBlog.authorImage ? editingBlog.authorImage : null);
      
      // Transform form data to match API format
      const blogData = {
        title: formData.title,
        slug: formData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
        excerpt: formData.excerpt,
        content: (formData.contentSections || []).map(section => ({
          heading: section.heading,
          description: section.description,
          sub_sections: (section.subSections || []).map(sub => sub.heading)
        })),
        image: featuredImageBase64,
        author: formData.author,
        author_bio: formData.authorBio,
        author_image: authorImageBase64,
        published_at: formData.publishedDate ? new Date(formData.publishedDate).toISOString() : new Date().toISOString(),
        tags: formData.tags,
        is_published: formData.status === 'Published' ? 'published' : 'draft'
      };

      if (editingBlog) {
        // Update existing blog
        await apiService.updateBlog(editingBlog.id, blogData);
        setSuccess('Blog post updated successfully!');
      } else {
        // Create new blog
        await apiService.createBlog(blogData);
        setSuccess('Blog post created successfully!');
      }
      
      // Refresh blogs list
      await fetchBlogs();
      
      setShowModal(false);
      setEditingBlog(null);
      setFormData({
        title: '',
        status: 'Draft',
        excerpt: '',
        author: '',
        authorImage: '',
        authorBio: '',
        featuredImage: '',
        publishedDate: '10/20/2025 12:00 AM',
        tags: [],
        contentSections: []
      });
      setNewTag('');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title || '',
      status: blog.status || 'Draft',
      excerpt: blog.excerpt || '',
      author: blog.author || '',
      authorImage: '',
      authorBio: blog.authorBio || '',
      featuredImage: '',
      publishedDate: blog.publishedAt ? new Date(blog.publishedAt).toISOString().slice(0, 16) : '',
      tags: Array.isArray(blog.tags) ? blog.tags : [],
      contentSections: Array.isArray(blog.content)
        ? blog.content.map(section => ({
            heading: section.heading || '',
            description: section.description || '',
            subSections: Array.isArray(section.subSections)
              ? section.subSections
              : (Array.isArray(section.sub_sections)
                  ? section.sub_sections.map(s =>
                      typeof s === 'string' ? { heading: s } : { heading: s?.heading || '' }
                    )
                  : [])
          }))
        : []
    });
    setShowModal(true);
  };

  const handleDelete = (blog) => {
    setBlogToDelete(blog);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!blogToDelete) return;
    try {
      setError(null);
      await apiService.deleteBlog(blogToDelete.id);
      setSuccess('Blog post deleted successfully!');
      setShowDeleteModal(false);
      setBlogToDelete(null);
      await fetchBlogs();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(error.message);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setBlogToDelete(null);
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      setError(null);
      // Find the blog to get its data
      const blog = blogs.find(b => b.id === id);
      if (!blog) return;
      
      // Update the blog status via API
      const blogData = {
        title: blog.title,
        slug: blog.slug,
        excerpt: blog.excerpt,
        content: blog.content,
        image: blog.featuredImage || null,
        author: blog.author,
        author_bio: blog.authorBio,
        author_image: blog.authorImage || null,
        published_at: blog.publishedAt,
        tags: blog.tags,
        is_published: newStatus === 'Published' ? 'published' : 'draft'
      };
      
      await apiService.updateBlog(id, blogData);
      
      // Update local state
      setBlogs(prev => prev.map(blog => 
        blog.id === id ? { ...blog, status: newStatus } : blog
      ));
      
      setSuccess('Blog status updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Blog Management - Admin Panel</title>
        <meta name="description" content="Manage blog posts and articles." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Blog Management</h1>
            <p className="text-gray-600">Manage your blog posts and articles</p>
          </div>
          <button
            onClick={() => {
              setEditingBlog(null);
              setFormData({
                title: '',
                status: 'Draft',
                excerpt: '',
                author: '',
                authorImage: '',
                authorBio: '',
                featuredImage: '',
                publishedDate: '10/20/2025 12:00 AM',
                tags: [],
                contentSections: []
              });
              setNewTag('');
              setShowModal(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Add New Post
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md">
            {success}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        {/* Blog List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Loading blogs...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={fetchBlogs}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No blog posts found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Author
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Excerpt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tags
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {blogs.map((blog, index) => (
                  <motion.tr
                    key={blog.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{blog.title}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {blog.author}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {blog.excerpt || 'No excerpt available'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {blog.tags && blog.tags.length > 0 ? (
                          blog.tags.slice(0, 2).map((tag, tagIndex) => (
                            <span
                              key={tagIndex}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {tag}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">No tags</span>
                        )}
                        {blog.tags && blog.tags.length > 2 && (
                          <span className="text-xs text-gray-500">
                            +{blog.tags.length - 2} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={blog.status}
                        onChange={(e) => handleStatusChange(blog.id, e.target.value)}
                        className={`text-xs font-medium px-2 py-1 rounded-full ${
                          blog.status === 'Published' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        <option value="Draft">Draft</option>
                        <option value="Published">Published</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {blog.date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(blog)}
                          className="text-blue-600 hover:text-blue-900 p-2 rounded-md hover:bg-blue-50"
                          title="Edit Blog"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(blog)}
                          className="text-red-600 hover:text-red-900 p-2 rounded-md hover:bg-red-50"
                          title="Delete Blog"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-4 mx-auto p-6 border w-full max-w-4xl shadow-lg rounded-lg bg-white">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  Create New Blog Post
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Title and Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Enter blog title"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Draft">Draft</option>
                      <option value="Published">Published</option>
                    </select>
                  </div>
                </div>

                {/* Excerpt */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Excerpt *
                  </label>
                  <textarea
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    placeholder="Enter blog excerpt"
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                    required
                  />
                </div>

                {/* Author and Author Image */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Author *
                    </label>
                    <input
                      type="text"
                      name="author"
                      value={formData.author}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Author Image
                    </label>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <input
                          type="file"
                          name="authorImage"
                          onChange={handleFileChange}
                          accept="image/jpeg,image/png,image/gif"
                          className="hidden"
                          id="authorImage"
                        />
                        <label
                          htmlFor="authorImage"
                          className="px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50"
                        >
                          Choose file
                        </label>
                        <span className="ml-2 text-sm text-gray-500">
                          {formData.authorImage ? formData.authorImage.name : 'No file chosen'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        Upload an image file (JPG, PNG, GIF) - Max 5MB
                      </p>
                    </div>
                  </div>
                </div>

                {/* Author Bio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Author Bio *
                  </label>
                  <textarea
                    name="authorBio"
                    value={formData.authorBio}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                    required
                  />
                </div>

                {/* Featured Image and Published Date */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Featured Image
                    </label>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <input
                          type="file"
                          name="featuredImage"
                          onChange={handleFileChange}
                          accept="image/jpeg,image/png,image/gif"
                          className="hidden"
                          id="featuredImage"
                        />
                        <label
                          htmlFor="featuredImage"
                          className="px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50"
                        >
                          Choose file
                        </label>
                        <span className="ml-2 text-sm text-gray-500">
                          {formData.featuredImage ? formData.featuredImage.name : 'No file chosen'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        Upload an image file (JPG, PNG, GIF) - Max 5MB
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Published Date
                    </label>
                    <input
                      type="datetime-local"
                      name="publishedDate"
                      value={formData.publishedDate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tags
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Enter tag and press Enter"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    />
                    <button
                      type="button"
                      onClick={addTag}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Add
                    </button>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-sm flex items-center gap-1"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(index)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Content Sections */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content Sections *
                  </label>
                  <button
                    type="button"
                    onClick={addContentSection}
                    className="mb-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    + Add Section
                  </button>
                  
                  {(formData.contentSections || []).map((section, sectionIndex) => (
                    <div key={sectionIndex} className="mb-6 p-4 border border-gray-200 rounded-lg">
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Heading *
                        </label>
                        <input
                          type="text"
                          value={section.heading}
                          onChange={(e) => updateContentSection(sectionIndex, 'heading', e.target.value)}
                          placeholder="Enter section heading"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description *
                        </label>
                        <textarea
                          value={section.description}
                          onChange={(e) => updateContentSection(sectionIndex, 'description', e.target.value)}
                          placeholder="Enter section description"
                          rows="3"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Sub-sections
                        </label>
                        <button
                          type="button"
                          onClick={() => addSubSection(sectionIndex)}
                          className="mb-2 px-3 py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm"
                        >
                          + Add Sub-section
                        </button>
                        
                        {(section.subSections || []).map((subSection, subIndex) => (
                          <div key={subIndex} className="ml-4 mb-3 p-3 bg-gray-50 rounded">
                            <div className="mb-2">
                              <input
                                type="text"
                                value={subSection.heading ?? subSection}
                                onChange={(e) => updateSubSection(sectionIndex, subIndex, 'heading', e.target.value)}
                                placeholder="Enter sub-section points"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer Buttons */}
                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Create Blog
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 mt-1">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M4.93 4.93a10.5 10.5 0 1114.14 14.14A10.5 10.5 0 014.93 4.93z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Delete Blog Post</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Are you sure you want to delete '{blogToDelete?.title}'? This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button onClick={cancelDelete} className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={confirmDelete} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManagement;
